import { useRef, useState, useEffect } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Eraser, PenLine, Upload, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";

export default function SignaturePad({ onChange, testid = "signature" }) {
  const [mode, setMode] = useState("draw");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || mode !== "draw") return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1B2A6B";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [mode]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return [(p.clientX - r.left) * (canvasRef.current.width / r.width), (p.clientY - r.top) * (canvasRef.current.height / r.height)];
  };
  const start = (e) => { drawing.current = true; const [x, y] = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const [x, y] = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); dirty.current = true; };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    if (c) { const ctx = c.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height); }
    dirty.current = false;
    setPreview(null);
    onChange(null);
  };

  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "signature.png");
      const { data } = await api.post("/approvals/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      setPreview(URL.createObjectURL(blob));
      toast.success("Signature captured");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const saveDrawing = () => {
    if (!dirty.current) { toast.error("Please draw your signature first"); return; }
    canvasRef.current.toBlob((b) => b && uploadBlob(b), "image/png");
  };

  const uploadFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Signature must be an image"); return; }
    uploadBlob(f);
  };

  const modeBtn = (m, label, Icon) => (
    <button type="button" data-testid={`${testid}-mode-${m}`} onClick={() => { setMode(m); clear(); }}
      className={cn("h-7 px-2.5 flex items-center gap-1.5 text-xs rounded-md transition-colors",
        mode === m ? "bg-[#1B2A6B] text-white" : "text-muted-foreground hover:bg-muted")}>
      <Icon size={13} />{label}
    </button>
  );

  return (
    <div className="space-y-2" data-testid={`${testid}-pad`}>
      <div className="flex items-center gap-1.5">
        {modeBtn("draw", "Draw", PenLine)}
        {modeBtn("upload", "Upload image", Upload)}
      </div>
      {mode === "draw" ? (
        <div>
          <canvas ref={canvasRef} width={560} height={160} data-testid={`${testid}-canvas`}
            className="w-full h-32 rounded-md border border-border bg-white touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
          <div className="flex justify-end gap-2 mt-1.5">
            <button type="button" data-testid={`${testid}-clear`} onClick={clear}
              className="h-7 px-2.5 flex items-center gap-1.5 text-xs rounded-md border border-border hover:bg-muted transition-colors">
              <Eraser size={12} />Clear
            </button>
            <button type="button" data-testid={`${testid}-save`} onClick={saveDrawing} disabled={uploading}
              className="h-7 px-2.5 flex items-center gap-1.5 text-xs rounded-md bg-[#1B2A6B] text-white hover:bg-[#16224f] transition-colors disabled:opacity-60">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : null}Use this signature
            </button>
          </div>
        </div>
      ) : (
        <label data-testid={`${testid}-upload-zone`}
          className="flex flex-col items-center justify-center gap-1.5 h-32 rounded-md border-2 border-dashed border-border hover:border-[#1B2A6B]/50 cursor-pointer transition-colors text-muted-foreground">
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          <span className="text-xs">{uploading ? "Uploading…" : "Click to upload signature image (PNG/JPG)"}</span>
          <input type="file" accept="image/*" className="hidden" data-testid={`${testid}-upload-input`} onChange={uploadFile} />
        </label>
      )}
      {preview && (
        <div className="flex items-center gap-2" data-testid={`${testid}-preview`}>
          <img src={preview} alt="Signature" className="h-10 rounded border border-border bg-white" />
          <span className="text-xs text-[#10B981] font-medium">Signature ready</span>
        </div>
      )}
    </div>
  );
}
